import type { WallAssembly } from "building-types/walls/wallAssemblyTypes";
import type { WallSection } from "building-types/walls/wallDesciptorTypes";
import type { HalfEdgeOffsets } from "building-types/walls/wallEndsTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { VoidWall, WallPath, WallLayer } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import { dynamicBuildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { DIST_EPSILON } from "geom/core";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { wallMassBaseCrossSection } from "./solidWallCrossSectionUtils";
import { wallMassCrossSectionLayer } from "./wallLayers";
import { wallSidePathDomainNew } from "./wallSideUtils";

export function voidWallFromHalfEdgeOffsets(
  id: string,
  path: WallPath,
  assembly: WallAssembly,
  floorElevation: number,
  section: WallSection,
  offsets: HalfEdgeOffsets,
  layers: WallLayer[] = [],
): VoidWall {
  const { leftOffset, rightOffset } = offsets;
  const instanceId = path.leftRoom?.instanceId ?? path.rightRoom?.instanceId;

  return {
    ...dynamicBuildingEntityBaseNew("WALL", id),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    wallType: WallType.VOID,
    wallAssembly: assembly,
    path,
    section,
    floorElevation,
    leftOffset,
    rightOffset,
    layers,
    leftDomain: wallSidePathDomainNew(0, 1),
    rightDomain: wallSidePathDomainNew(0, 1),
  };
}

export function voidWallCrossSection({
  wall,
  // TODO
  // includeDoors = true,
  // includeWindows = true,
  // includeWallCuts = true,
  includeZeroThicknessEnds = true,
  closedPolyLines = false,
  wallLayer = WallLayerType.SUBSTRATE,
  wallLOD = WallLOD.L2,
  cutPlane,
}: {
  wall: VoidWall;
  wallLayer?: WallLayerType;
  wallLOD?: WallLOD;
  includeDoors?: boolean;
  includeWindows?: boolean;
  includeWallCuts?: boolean;
  includeZeroThicknessEnds?: boolean;
  closedPolyLines?: boolean;
  cutPlane?: number;
}): PolyLine2[] {
  if (!(wallLayer !== WallLayerType.FINISH)) {
    console.assert(false, `
  FINISH wall layers should only be used for debugging purposes.
  They do not accurately reflect the true cross section of a wall.
  wallSideFinishMassCrossSection should be used instead.
  `);
  }

  const crossSectionMass = wallMassCrossSectionLayer(wall, wallLayer, wallLOD, cutPlane);
  if (!crossSectionMass || crossSectionMass.wallType === WallType.VOID) {
    return [];
  }
  const { leftEdge, rightEdge, leftOffset, rightOffset } = crossSectionMass;
  if (leftOffset + rightOffset < DIST_EPSILON) {
    if (!includeZeroThicknessEnds) {
      return [];
    }
    // this wall has 0 thickness at this cutPlane
    const botLeftX = leftEdge[2];
    const botLeftY = leftEdge[3];

    const topLeftX = leftEdge[0];
    const topLeftY = leftEdge[1];

    const botRightX = rightEdge[0];
    const botRightY = rightEdge[1];

    const topRightX = rightEdge[2];
    const topRightY = rightEdge[3];

    return [
      polyLine2New([botLeftX, botLeftY, botRightX, botRightY]),
      polyLine2New([topLeftX, topLeftY, topRightX, topRightY]),
    ];
  }
  return wallMassBaseCrossSection(crossSectionMass, closedPolyLines);
}
